import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

function Avatar({ name, size = 40 }) {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'var(--gold-dim)', border: '1.5px solid var(--gold)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--gold)', fontFamily: 'DM Mono, monospace',
      fontSize: size * 0.32, fontWeight: 600, flexShrink: 0
    }}>
      {initials}
    </div>
  )
}

function StatCard({ label, value, sub }) {
  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 36, fontFamily: 'Cormorant Garamond, serif', fontWeight: 700, color: 'var(--gold)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

export default function Accesos() {
  const { usuario } = useAuth()
  const [query, setQuery] = useState('')
  const [persona, setPersona] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [oficinas, setOficinas] = useState([])
  const [oficinaId, setOficinaId] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)
  const [recentLogs, setRecentLogs] = useState([])
  const [showNewForm, setShowNewForm] = useState(false)
  const [newPersona, setNewPersona] = useState({ nombre: '', apellido: '', dni: '', telefono: '' })
  const [nombreCompleto, setNombreCompleto] = useState('') // Input único para dictado
  const [personaLogs, setPersonaLogs] = useState([])
  const [notas, setNotas] = useState([])
  const [nuevaNota, setNuevaNota] = useState('')
  const [savingNota, setSavingNota] = useState(false)
  const [stats, setStats] = useState({ hoy: 0, mes: 0, anio: 0 })
  const inputRef = useRef()

  const edificioId = usuario?.rol === 'admin' ? null : usuario?.edificio_id

  // Capitalizar: primera letra mayúscula, resto minúscula
  const capitalize = (text) => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Auto-dividir nombre completo en nombre y apellido
  const splitNombreCompleto = (texto) => {
    const partes = texto.trim().split(/\s+/).filter(Boolean)
    if (partes.length === 0) return { nombre: '', apellido: '' }
    if (partes.length === 1) return { nombre: capitalize(partes[0]), apellido: '' }
    // Última palabra = apellido, el resto = nombre
    const apellido = capitalize(partes.pop())
    const nombre = capitalize(partes.join(' '))
    return { nombre, apellido }
  }

  const handleNombreCompletoBlur = () => {
    if (nombreCompleto.trim()) {
      const { nombre, apellido } = splitNombreCompleto(nombreCompleto)
      setNewPersona(p => ({ ...p, nombre, apellido }))
    }
  }

  useEffect(() => {
    fetchOficinas(); fetchRecentLogs(); fetchStats()
    inputRef.current?.focus()
  }, [])

  const fetchOficinas = async () => {
    let q = supabase.from('oficinas').select('id, numero, titular, email_contacto, email_contacto_2, edificio_id, edificios(nombre)').eq('activa', true).order('numero')
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setOficinas(data || [])
  }

  const fetchRecentLogs = async () => {
    const startOfDay = new Date(); startOfDay.setHours(0,0,0,0)
    let q = supabase.from('accesos').select('*, personas(*), oficinas(*), edificios(nombre)')
      .gte('timestamp', startOfDay.toISOString()).order('timestamp', { ascending: false }).limit(15)
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setRecentLogs(data || [])
  }

  const fetchStats = async () => {
    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
    const startOfYear = new Date(now.getFullYear(), 0, 1).toISOString()
    const buildQ = (from) => {
      let q = supabase.from('accesos').select('id', { count: 'exact', head: true }).gte('timestamp', from)
      if (edificioId) q = q.eq('edificio_id', edificioId)
      return q
    }
    const [{ count: hoy }, { count: mes }, { count: anio }] = await Promise.all([buildQ(startOfDay), buildQ(startOfMonth), buildQ(startOfYear)])
    setStats({ hoy: hoy || 0, mes: mes || 0, anio: anio || 0 })
  }

  const fetchPersonaLogs = async (personaId) => {
    let q = supabase.from('accesos').select('*, oficinas(numero, titular), edificios(nombre)')
      .eq('persona_id', personaId).order('timestamp', { ascending: false }).limit(5)
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setPersonaLogs(data || [])
  }

  const fetchNotas = async (personaId) => {
    const { data } = await supabase.from('notas_personas').select('*, usuarios(nombre)')
      .eq('persona_id', personaId).order('created_at', { ascending: false })
    setNotas(data || [])
  }

  const handleSearch = async () => {
    const q = query.trim()
    if (!q) return
    setLoading(true); setPersona(null); setNotFound(false); setShowNewForm(false); setPersonaLogs([]); setNotas([])
    let dbQuery = supabase.from('personas').select('*')
    if (/^\d+$/.test(q)) dbQuery = dbQuery.eq('dni', q)
    else dbQuery = dbQuery.or(`nombre.ilike.%${q}%,apellido.ilike.%${q}%`)
    const { data } = await dbQuery.limit(1).single()
    if (data) { setPersona(data); fetchPersonaLogs(data.id); fetchNotas(data.id) }
    else { setNotFound(true); setNewPersona(p => ({ ...p, dni: /^\d+$/.test(q) ? q : '', nombre: /^\d+$/.test(q) ? '' : q })) }
    setLoading(false)
  }

  const sendEmail = async (oficina, datos) => {
    const emails = [oficina.email_contacto, oficina.email_contacto_2].filter(Boolean)
    if (emails.length === 0) return
    for (const email of emails) {
      try {
        await fetch('/api/send-email', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'acceso', destinatario_email: email, oficina: oficina.numero, oficina_titular: oficina.titular, edificio: oficina.edificios?.nombre || '', datos })
        })
      } catch (e) { console.error(e) }
    }
  }

  const handleRegistrar = async () => {
    if (!persona || !oficinaId) return
    setLoading(true)
    const oficina = oficinas.find(o => o.id === oficinaId)
    const { error } = await supabase.from('accesos').insert({ persona_id: persona.id, oficina_id: oficinaId, edificio_id: oficina.edificio_id, usuario_id: usuario?.id })
    if (!error) {
      sendEmail(oficina, { nombre: persona.nombre, apellido: persona.apellido, dni: persona.dni })
      setSuccess(`${persona.nombre} ${persona.apellido} → Of. ${oficina.numero} ${oficina.titular}`)
      setTimeout(() => setSuccess(null), 4000)
      setPersona(null); setQuery(''); setOficinaId(''); setPersonaLogs([]); setNotas([])
      fetchRecentLogs(); fetchStats(); inputRef.current?.focus()
    }
    setLoading(false)
  }

  const handleAddPersona = async () => {
    if (!newPersona.nombre || !newPersona.apellido || !newPersona.dni) return
    setLoading(true)
    const { data, error } = await supabase.from('personas').insert(newPersona).select().single()
    if (!error && data) { setPersona(data); setNotFound(false); setShowNewForm(false); setPersonaLogs([]); setNotas([]) }
    setLoading(false)
  }

  const handleAgregarNota = async () => {
    if (!nuevaNota.trim() || !persona) return
    setSavingNota(true)
    await supabase.from('notas_personas').insert({ persona_id: persona.id, usuario_id: usuario?.id, texto: nuevaNota.trim() })
    setNuevaNota(''); fetchNotas(persona.id); setSavingNota(false)
  }

  const formatTime = (ts) => new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  const formatDate = (ts) => new Date(ts).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
  const formatFull = (ts) => {
    const d = new Date(ts)
    return d.toDateString() === new Date().toDateString() ? `hoy ${formatTime(ts)}` : `${formatDate(ts)} ${formatTime(ts)}`
  }

  return (
    <div className="fade-in">
      {success && (
        <div style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--green)', color: 'white', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', animation: 'slideDown 0.3s ease', whiteSpace: 'nowrap' }}>
          ✓ Ingreso registrado · {success}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }} className="mobile-gap-sm">
        <StatCard label="INGRESOS HOY" value={stats.hoy} sub={new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })} />
        <StatCard label="ESTE MES" value={stats.mes} sub={new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })} />
        <StatCard label="ESTE AÑO" value={stats.anio} sub={String(new Date().getFullYear())} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }} className="mobile-grid-1">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 16 }}>BUSCAR PERSONA</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input ref={inputRef} className="input" placeholder="DNI o nombre..." value={query}
                onChange={e => { setQuery(e.target.value); setPersona(null); setNotFound(false) }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()} style={{ flex: 1 }} />
              <button className="btn btn-gold" onClick={handleSearch} disabled={loading}>{loading ? '...' : 'Buscar'}</button>
            </div>
          </div>

          {persona && (
            <div className="card fade-in" style={{ borderColor: 'var(--gold)', borderWidth: 1.5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                <Avatar name={`${persona.nombre} ${persona.apellido}`} size={52} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 20, fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>{persona.nombre} {persona.apellido}</div>
                  <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>DNI {persona.dni}{persona.telefono && ` · ${persona.telefono}`}</div>
                  <div style={{ fontSize: 12, marginTop: 4, color: personaLogs.length > 0 ? 'var(--gold)' : 'var(--text3)' }}>
                    {personaLogs.length > 0 ? `Última visita: ${formatFull(personaLogs[0].timestamp)} · Of. ${personaLogs[0].oficinas?.numero}` : 'Sin visitas previas en este edificio'}
                  </div>
                </div>
              </div>

              {personaLogs.length > 0 && (
                <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 10 }}>VISITAS ANTERIORES EN ESTE EDIFICIO</div>
                  {personaLogs.map(log => (
                    <div key={log.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--text2)' }}>Of. {log.oficinas?.numero} · {log.oficinas?.titular}</span>
                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--text3)' }}>{formatFull(log.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>VISITA A OFICINA</label>
                <select className="input" value={oficinaId} onChange={e => setOficinaId(e.target.value)} style={{ marginBottom: 10 }}>
                  <option value="">Seleccioná una oficina...</option>
                  {oficinas.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.titular}</option>)}
                </select>
                <button className="btn btn-gold" style={{ width: '100%', padding: 14, fontSize: 14 }} onClick={handleRegistrar} disabled={!oficinaId || loading}>↑ Registrar Ingreso</button>
              </div>

              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 10 }}>NOTAS INTERNAS</div>
                {notas.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, maxHeight: 160, overflowY: 'auto' }}>
                    {notas.map(nota => (
                      <div key={nota.id} style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 12px' }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>{nota.texto}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>{formatFull(nota.created_at)} · {nota.usuarios?.nombre || 'Recepción'}</div>
                      </div>
                    ))}
                  </div>
                )}
                {notas.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>Sin notas sobre esta persona.</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="input" placeholder="Agregar nota..." value={nuevaNota}
                    onChange={e => setNuevaNota(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAgregarNota()} style={{ flex: 1, fontSize: 13 }} />
                  <button className="btn btn-ghost btn-sm" onClick={handleAgregarNota} disabled={savingNota || !nuevaNota.trim()}>+ Agregar</button>
                </div>
              </div>
            </div>
          )}

          {notFound && !showNewForm && (
            <div className="card fade-in" style={{ borderColor: 'rgba(201,168,76,0.3)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Persona no encontrada</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>No existe nadie con ese DNI o nombre en el sistema.</div>
              <button className="btn btn-gold btn-sm" onClick={() => setShowNewForm(true)}>+ Agregar persona nueva</button>
            </div>
          )}

          {showNewForm && (
            <div className="card fade-in">
              <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 16 }}>NUEVA PERSONA</div>
              
              {/* Input único para dictado rápido */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>NOMBRE COMPLETO (dictado rápido)</label>
                <input 
                  className="input" 
                  placeholder="Ej: García Juan o Juan García (como te lo dicten)" 
                  value={nombreCompleto}
                  onChange={e => setNombreCompleto(e.target.value)}
                  onBlur={handleNombreCompletoBlur}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleNombreCompletoBlur()
                      e.preventDefault()
                    }
                  }}
                  style={{ fontSize: 15, fontWeight: 500 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontStyle: 'italic' }}>
                  Escribí el nombre como te lo dicten. Se dividirá automático al salir del campo.
                </div>
              </div>

              {/* Campos divididos (editables) */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>NOMBRE *</label>
                    <input className="input" placeholder="Nombre" value={newPersona.nombre} 
                      onChange={e => setNewPersona(p => ({ ...p, nombre: e.target.value }))}
                      onBlur={e => {
                        if (e.target.value.trim()) {
                          setNewPersona(p => ({ ...p, nombre: capitalize(e.target.value.trim()) }))
                        }
                      }} />
                  </div>
                  
                  {/* Botón intercambiar */}
                  <button 
                    type="button"
                    onClick={() => setNewPersona(p => ({ ...p, nombre: p.apellido, apellido: p.nombre }))}
                    style={{
                      background: 'var(--bg3)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      color: 'var(--text2)',
                      fontSize: 16,
                      transition: 'all 0.15s',
                      marginBottom: 0,
                      height: 42
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold-dim)'; e.currentTarget.style.color = 'var(--gold)'; e.currentTarget.style.borderColor = 'var(--gold)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.borderColor = 'var(--border)' }}
                    title="Intercambiar nombre y apellido"
                  >
                    ⇄
                  </button>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>APELLIDO *</label>
                    <input className="input" placeholder="Apellido" value={newPersona.apellido} 
                      onChange={e => setNewPersona(p => ({ ...p, apellido: e.target.value }))}
                      onBlur={e => {
                        if (e.target.value.trim()) {
                          setNewPersona(p => ({ ...p, apellido: capitalize(e.target.value.trim()) }))
                        }
                      }} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }} className="mobile-grid-2">
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>DNI *</label>
                  <input className="input" placeholder="DNI" value={newPersona.dni} 
                    onChange={e => setNewPersona(p => ({ ...p, dni: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>TELÉFONO</label>
                  <input className="input" placeholder="Opcional" value={newPersona.telefono} 
                    onChange={e => setNewPersona(p => ({ ...p, telefono: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => { setShowNewForm(false); setNombreCompleto('') }}>Cancelar</button>
                <button className="btn btn-gold btn-sm" onClick={handleAddPersona} disabled={loading || !newPersona.nombre || !newPersona.apellido || !newPersona.dni}>Guardar y continuar →</button>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 16 }}>ÚLTIMOS INGRESOS HOY</div>
          {recentLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 13 }}>Sin ingresos registrados hoy</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentLogs.map(log => (
                <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--bg3)' }}>
                  <Avatar name={`${log.personas?.nombre} ${log.personas?.apellido}`} size={34} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.personas?.nombre} {log.personas?.apellido}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Of. {log.oficinas?.numero} · {log.oficinas?.titular}
                      {usuario?.rol === 'admin' && <span style={{ color: 'var(--gold)', marginLeft: 4 }}>· {log.edificios?.nombre?.replace('Essence ', '')}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, color: 'var(--green)' }}>{formatTime(log.timestamp)}</div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--text3)' }}>{formatDate(log.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
